import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { parseLocalDate, toLocalDateString } from '../../utils/payrollPeriodLocal';
import { formatCompanyDateLocale } from '../../utils/companyDateFormat';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Calendar that selects a pay period: clicking any day selects that period. */
export function WeekPickerCalendar({
  value,
  onChange,
  onClose,
  weekStartsOn,
  resolvePeriodStart,
  isDayInSelection,
}: {
  value: string;
  onChange: (periodStartYmd: string) => void;
  onClose: () => void;
  weekStartsOn: number;
  /** Defaults to week-start for `weekStartsOn`. */
  resolvePeriodStart?: (date: Date) => string;
  /** Defaults to “same week as value”. */
  isDayInSelection?: (date: Date, selectedStartYmd: string) => boolean;
}) {
  const getWeekStart = (date: Date): string => {
    const day = date.getDay();
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7;
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - diff);
    return toLocalDateString(weekStart);
  };

  const periodStartFor = resolvePeriodStart ?? getWeekStart;
  const dayInSelection =
    isDayInSelection ??
    ((date: Date, selectedStartYmd: string) => periodStartFor(date) === selectedStartYmd);

  const [viewDate, setViewDate] = useState(() => {
    if (value) return parseLocalDate(value);
    return new Date();
  });

  useEffect(() => {
    if (value) setViewDate(parseLocalDate(value));
  }, [value]);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthLabel = formatCompanyDateLocale(viewDate, { month: 'long', year: 'numeric' });

  const firstCell = new Date(year, month, 1);
  while (firstCell.getDay() !== weekStartsOn) {
    firstCell.setDate(firstCell.getDate() - 1);
  }
  const cells: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(firstCell);
    d.setDate(firstCell.getDate() + i);
    cells.push(d);
  }

  const dayNames = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < 7; i++) out.push(DAY_NAMES[(weekStartsOn + i) % 7]);
    return out;
  }, [weekStartsOn]);

  const handlePrevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const handleNextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const handleDayClick = (date: Date) => {
    onChange(periodStartFor(date));
    onClose();
  };

  return (
    <div className="inline-block border border-neutral-200 rounded-lg p-3 bg-white">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
          aria-label="Previous month"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="text-sm font-medium text-neutral-800">{monthLabel}</span>
        <button
          type="button"
          onClick={handleNextMonth}
          className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
          aria-label="Next month"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center text-xs">
        {dayNames.map((name) => (
          <div key={name} className="py-1 font-medium text-neutral-500">
            {name}
          </div>
        ))}
        {cells.map((d) => {
          const ymd = toLocalDateString(d);
          const isSelectedStart = Boolean(value) && ymd === value;
          const isInRange = Boolean(value) && dayInSelection(d, value);
          const isCurrentMonth = d.getMonth() === month;
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => handleDayClick(d)}
              className={`
                py-1.5 rounded text-sm
                ${!isCurrentMonth ? 'text-neutral-300' : 'text-neutral-800'}
                ${isSelectedStart ? 'bg-blue-600 text-white' : isInRange ? 'bg-blue-100 text-blue-900' : 'hover:bg-neutral-100'}
              `}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}
